import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, X, Link as LinkIcon, Image as ImageIcon } from "lucide-react";
import { api } from "../lib/api";

/**
 * ImageUpload
 * - value: current image URL (may be relative /api/files/... or absolute)
 * - onChange: called with new URL string
 * - aspect: "cover" or "square"
 */
export default function ImageUpload({ value, onChange, aspect = "cover", testId = "image-upload" }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("upload"); // "upload" | "link"
  const [urlDraft, setUrlDraft] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Falling back to "" keeps relative /api/files/... URLs same-origin when the
  // env var is unset (see lib/api.js), avoiding `undefined/api/...` previews.
  const displayUrl = value?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${value}` : value;

  const doUpload = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) {
      return toast.error("Only png, jpg, webp, or gif");
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("File too large (max 5 MB)");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const useUrl = () => {
    if (!urlDraft.trim()) return;
    onChange(urlDraft.trim());
    setUrlDraft("");
    toast.success("Link applied");
  };

  const clear = () => { onChange(""); setUrlDraft(""); };

  const aspectClass = aspect === "square" ? "aspect-square" : "aspect-[16/9]";

  return (
    <div className="space-y-2" data-testid={testId}>
      {value ? (
        <div className={`relative ${aspectClass} rounded-2xl overflow-hidden border border-white/60 bg-white/40`}>
          <img src={displayUrl} alt="preview" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
          <button onClick={clear} type="button" className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/85 backdrop-blur border border-white/60 flex items-center justify-center hover:text-destructive" data-testid={`${testId}-clear`} aria-label="Remove image">
            <X size={13} />
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-1 p-1 bg-white/40 rounded-xl border border-white/60 w-fit">
            <ModeChip active={mode === "upload"} onClick={() => setMode("upload")} icon={Upload} testId={`${testId}-tab-upload`}>Upload</ModeChip>
            <ModeChip active={mode === "link"} onClick={() => setMode("link")} icon={LinkIcon} testId={`${testId}-tab-link`}>Paste link</ModeChip>
          </div>
          {mode === "upload" ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`${aspectClass} rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition ${dragOver ? "bg-white/80" : "bg-white/30 hover:bg-white/50"}`}
              style={{ borderColor: dragOver ? "hsl(var(--vd-green))" : "hsl(220 10% 78%)" }}
              data-testid={`${testId}-dropzone`}
            >
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center mb-3" style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}>
                <ImageIcon size={18} />
              </div>
              <div className="text-sm font-medium">{busy ? "Uploading…" : "Drop an image or click to browse"}</div>
              <div className="text-[11px] text-muted-foreground font-mono mt-1">PNG · JPG · WEBP · max 5 MB</div>
              <input ref={inputRef} type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => doUpload(e.target.files?.[0])} data-testid={`${testId}-input`} />
            </div>
          ) : (
            <div className={`${aspectClass} rounded-2xl border border-white/60 bg-white/40 flex flex-col items-center justify-center p-6`}>
              <input className="input-field" placeholder="https://…" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} data-testid={`${testId}-url-input`} />
              <button onClick={useUrl} type="button" className="btn-primary mt-3 text-sm" data-testid={`${testId}-url-apply`}>Use this link</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ModeChip({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button type="button" onClick={onClick} className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition ${active ? "font-medium" : "text-muted-foreground"}`} style={{ background: active ? "hsl(var(--vd-green))" : "transparent", color: active ? "white" : undefined }} data-testid={testId}>
      <Icon size={11} />{children}
    </button>
  );
}

export { ImageUpload };
